import React from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { Card, CardLabel } from "../ui/RecordLayout";

/**
 * What the sheet said about the thumb hole's shape.
 *
 * Six values -- the hole type and five oval numbers -- that used to sit at
 * the bottom of the review as five more text rows, indistinguishable from
 * everything else and easy to skim past. On a bowler's screen they have their
 * own card in the right-hand column, which is where the operator already
 * looks for them, so that is where they are here.
 *
 * It is worth having: shop forms print an "Oval Information" box with the
 * angle and width, and until this card existed those numbers were read off
 * the paper and then had nowhere to be seen.
 *
 * Every cell opens the sheet's own picker, the same one the bowler screen
 * uses -- including the four that have no box on the drawing and so could not
 * be reached any other way.
 */

const Toggle = styled.div`
  display: inline-flex;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 10px;
`;

const Side = styled.button`
  /* Touch-first: tapped on a tablet at a shop counter. */
  min-height: 40px;
  padding: 4px 18px;
  border: none;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  background: ${(p) => (p.$on ? "#00187e" : "#ffffff")};
  color: ${(p) => (p.$on ? "#ffffff" : "#6b7280")};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
`;

const Cell = styled.button`
  border: 1px solid ${(p) => (p.$flagged ? "#f5a524" : "#e5e7eb")};
  background: ${(p) => (p.$flagged ? "#fef5e1" : "#ffffff")};
  border-radius: 8px;
  /* Touch-first: tapped on a tablet at a shop counter. */
  min-height: 52px;
  padding: 5px 8px;
  text-align: center;

  &:hover {
    background: ${(p) => (p.$flagged ? "#fdecc8" : "#f5f6fa")};
  }
`;

const Key = styled.div`
  font-size: 8.5px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #6b7280;
`;

const Val = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: #00187e;
`;

const Blank = styled(Val)`
  color: #b0851f;
  font-weight: 500;
  font-style: italic;
`;

const OVAL_FIELDS = [
  ["thm_oval_starting_bit", "specsheetScan.oval.startingBit"],
  ["thm_oval_width", "specsheetScan.oval.width"],
  ["thm_oval_degree", "specsheetScan.oval.degree"],
  ["thm_oval_taper", "specsheetScan.oval.taper"],
];

const ROUND_FIELDS = [["thm_round_taper", "specsheetScan.oval.roundTaper"]];

const ScanOvalCard = ({ values = {}, flagged, onPick }) => {
  const { t } = useTranslation();

  const isOval = values.thm_type === "Oval";
  const fields = isOval ? OVAL_FIELDS : ROUND_FIELDS;

  // Nothing about the thumb hole was read at all -- no type, no numbers. A
  // card of five empty cells would be five invitations to invent values the
  // sheet never carried.
  const anything =
    values.thm_type ||
    OVAL_FIELDS.concat(ROUND_FIELDS).some(([key]) => values[key]);
  if (!anything) return null;

  return (
    <Card>
      <CardLabel>{t("specsheetScan.oval.title")}</CardLabel>
      <Toggle>
        <Side type="button" $on={!isOval} onClick={() => onPick("thm_type")}>
          {t("specsheetScan.oval.round")}
        </Side>
        <Side type="button" $on={isOval} onClick={() => onPick("thm_type")}>
          {t("specsheetScan.oval.oval")}
        </Side>
      </Toggle>
      <Grid>
        {fields.map(([key, labelKey]) => (
          <Cell
            key={key}
            type="button"
            $flagged={flagged.has(key)}
            onClick={() => onPick(key)}
          >
            <Key>{t(labelKey)}</Key>
            {values[key] ? (
              <Val>{values[key]}</Val>
            ) : (
              <Blank>{t("specsheetScan.needs.blank")}</Blank>
            )}
          </Cell>
        ))}
      </Grid>
    </Card>
  );
};

export default ScanOvalCard;
